import { Outlet } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import HelpWidget from '../components/HelpWidget';
import CommandPalette from '../components/CommandPalette';
import { useAdminData } from '../hooks/useAdminData';
import { useAuth } from '../context/AuthContext';
import ErrorBoundary from '../components/ErrorBoundary';
import { PENDING_APP_STAGES } from '../constants/enums';
import { dispatchAlertEmails } from '../services/alertEmailDispatcher';
import LoadingState from '../components/states/LoadingState';
import Footer from '../components/Footer';
// Context + hook are in a separate module so this file can be a pure
// components-only module for Vite Fast Refresh. Keeping `useAdminDataContext`
// here broke HMR — every edit in the admin graph triggered a full invalidation
// instead of a hot update, stranding the page in a loading state.
import { AdminDataContext } from './adminDataContext';

export default function AdminLayout() {
  const adminData = useAdminData();
  const { user } = useAuth();

  // Force admin-dark theme for admin views — matches the landing hero +
  // login aesthetic. Tokens defined in src/App.css under [data-theme="admin-dark"].
  // No cleanup-restore: prev/cleanup pattern can leave theme stale during
  // sibling-route transitions (e.g., /admin → /client → /admin would
  // sometimes leak the inner route's theme). Each layout sets its own
  // theme on mount; the next layout's mount overrides — clean handoff.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'admin-dark');
  }, []);

  // Phase 17.4 — dispatch SPL alert emails when alerts recompute. The
  // dispatcher applies severity filtering, quiet hours (10pm–7am local
  // suppress non-critical), and idempotency-keyed dedup so the same
  // alert doesn't fire twice in its window. Session-scope cache prevents
  // re-fire across re-renders; edge-function idempotency check provides
  // cross-session dedup via email_log.idempotency_key.
  //
  // Dependency uses alert dismissKeys joined as a string — only fires
  // when the alert SET changes, not on every render of useAdminData
  // (which re-memos with sharedNow on each render).
  const visibleAlerts = adminData.visibleAlerts || [];
  const alertKeysSig = visibleAlerts.map(a => a?.dismissKey || a?.type).join('|');
  useEffect(() => {
    if (!user?.id || !adminData.activeAdminOrgId || visibleAlerts.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await dispatchAlertEmails(visibleAlerts, {
          orgId: adminData.activeAdminOrgId,
          splUserId: user.id,
          splEmail: user.email,
          // Phase 17 Tier-2 §2.1 — pass recipient timezone for correct
          // quiet-hours computation (was using browser's local time).
          // user.user_metadata.timezone or fallback to detect from browser.
          splTimezone: user.user_metadata?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        });
        if (!cancelled && (result.dispatched > 0 || result.skipped > 0)) {
          // Light log so QA can verify the dispatch ran without inspecting network.
          console.debug(`[AlertEmailDispatcher] dispatched=${result.dispatched} skipped=${result.skipped}`);
        }
      } catch (err) {
        // Email dispatch must NEVER block the admin page.
        console.warn('[AlertEmailDispatcher] dispatch failed:', err?.message || err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alertKeysSig captures the alert set; including the array would over-fire
  }, [alertKeysSig, user?.id, user?.email, adminData.activeAdminOrgId]);

  const pendingAppCount = (adminData.applications || []).filter(a => PENDING_APP_STAGES.includes(a.stage)).length;
  const alertCount = visibleAlerts.length;

  return (
    <AdminDataContext.Provider value={adminData}>
      <div className="app">
        {/* A11y skip-link (B-13) — only visible when focused via keyboard Tab.
            Jumps past the sidebar chrome directly into the main content for
            screen-reader + keyboard-only users. */}
        <a
          href="#main-content"
          className="skip-link"
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            padding: '6px 12px',
            fontSize: 12,
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-primary)',
            background: 'var(--accent-teal, #5DCAA5)',
            borderRadius: 6,
            textDecoration: 'none',
            zIndex: 10000,
            transform: 'translateY(-200%)',
            transition: 'transform 0.15s',
          }}
          onFocus={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          onBlur={(e) => { e.currentTarget.style.transform = 'translateY(-200%)'; }}
        >
          Skip to main content
        </a>
        <AdminSidebar pendingAppCount={pendingAppCount} alertCount={alertCount} />
        <main
          id="main-content"
          className="main-content"
          tabIndex={-1}
          style={{ padding: '24px 32px', minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}
        >
          <div style={{ flex: 1 }}>
            <ErrorBoundary label="Admin Page">
              <Suspense fallback={<LoadingState kind="spinner" message="Loading…" ariaLabel="Loading admin page" style={{ padding: 40 }} />}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </div>
          {/* Phase 19.4 audit-fix (2026-04-29): global Footer on every authenticated page per scope §6 + GDPR Article 13 */}
          <Footer variant="default" />
        </main>
        {/* Day 5: in-app help + command palette. Both self-gated on
            authenticated / app-route so they don't leak onto /login etc. */}
        <HelpWidget />
        <CommandPalette />
      </div>
    </AdminDataContext.Provider>
  );
}
